import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { Column, UserColumn, ColumnSortDirection, ColumnSortEvent, TableStyleConfig, SelectedAction, TableStyle } from './data-table.interfaces';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SkinningService } from '../../helper/services/skinning.service';
//import { FillTheGapDirective } from '@app/@shared/directive/fill-the-gap/fill-the-gap.directive';
//import { IMeta } from '@app/@store/request-options.interface';


@Component({
    selector: 'ig-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    standalone: false
})
export class DataTableComponent<T extends object> implements OnInit, OnChanges {
  #skinningService = inject(SkinningService);
  protected readonly destroyed$ = new Subject<boolean>();

  @Input() tableStyle = 'default';

  @Input() columns?: Column[];
  @Input() data: T[] | undefined = [];
  @Input() userColumns?: UserColumn[];
  @Input() total = 0;
  @Input() busyRows?: T[] = [];
  @Input() sort?: string;
  @Input() selectable = true;
  @Input() multiSelect = true;
  @Input() allSelect = false;
  @Input() sortDirection: ColumnSortDirection | string = ColumnSortDirection.ASC;
  @Input() dataPrimaryKey = 'object_uuid';

  @Input() tableTitle?: string;
  @Input() showTotalCount: boolean = false;
  @Input() hideEmptyTable = true;
  @Input() emptyPlaceholder?: string;
  @Input() emptyPlaceholderHeadline?: string;
  @Input() emptyPlaceholderReadOnly?: string;
  @Input() emptyPlaceholderHeadlineReadOnly?: string;
  @Input() emptyImageSrc?: string;
  @Input() itemPlural = "Items";
  @Input() createBtn?: {
    icon?: string;
    label: string;
    secondary?: boolean;
    func: () => void;
  };
  @Input() createBtnInHeader = false;

  @Input() fillScreen?: boolean = true;

  @Input() viewportElement?: string = '.viewport';
  @Input() fixedHeaderOffset?: number = 0;
  @Input() fixedHeader?= false;
  @Input() fixedFirstColumn?= false;
  @Input() fixedLastColumn?= false;

  @Input() hideFilter = false;
  @Input() search?: string;
  @Input() searchBy?: string;
  @Input() filterPlaceholder?: string;
  @Input() selection?: T[];
  @Input() disabledSelection?: T[];

  @Input() showFavorite = true;
  @Input() showMakeFavAction = true;
  @Input() selectedActions?: SelectedAction[];
  @Input() readOnly = false;

  @Input() loading?: boolean;
  @Input() loadingNext?: boolean;

  @Input() hasHelperCell = false;
  @Input() jsHoverDisabled = false;
  @Input() disableContextMenu = false;

  /**
   * set to true when custom filters are enabled, and there should be no empty state with zero results
   */
  @Input() customFilterEnabled = false;

  @Input() meta$?: Observable<any | undefined>; // Observable<IMeta | undefined>;

  @Output() loadNext: EventEmitter<void> = new EventEmitter();

  @Output() selectionChange: EventEmitter<T[]> = new EventEmitter();
  @Output() changeSort: EventEmitter<ColumnSortEvent> = new EventEmitter();
  @Output() changeColumns: EventEmitter<UserColumn[]> = new EventEmitter();
  @Output() cellClick: EventEmitter<{ col: Column; row: T }> = new EventEmitter();
  @Output() doSearch = new EventEmitter();
  @Output() toggleFavorites = new EventEmitter();
  @Output() storeFavorites = new EventEmitter();
  @Output() storeUnFavorites = new EventEmitter();

  @ViewChild('igDataTableWrapperLevel0', { static: true }) wrapperLevel0?: ElementRef;

  tableStyleConfig: TableStyleConfig = {};

  toggleFavoritesEnabled = false;
  filterFavorites = false;
  meta?: any; //IMeta;

  constructor() {
    this.tableStyleConfig = this.getMinimumConfig();


  }

  ngOnInit(): void {
    if (this.meta$) {
      this.meta$.pipe(
        takeUntil(this.destroyed$),
      ).subscribe(meta => {
        this.meta = meta;
      });
    }

  }

  /**
   * @ignore
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['tableStyle']) {
      this.setTableStyle();
    }

    if (changes['data']) {
      this.checkData();
    }
  }

  /**
   * @ignore
   */
  checkData() {
    this.data = this.data?.filter((element) => {
      return element !== undefined && element !== null;
    });
  }

  getMinimumConfig(): TableStyleConfig {
    return {
      "tableHeight": 'auto',
      "fluidTableHeight": false,
      "frameless": true,
      "hoverShadow": true
    }
  }

  get tableStyleClass() {
    return { 'ig-panelbox': !this.tableStyleConfig.frameless, 'ig-data-table-wrapper--fullHeight': this.fillScreen, ['ig-table-style--' + this.tableStyle]: true }
  }

  setTableStyle() {
    let _config: TableStyleConfig = {};
    const defaultColors = this.#skinningService.getStylePropertiesDefault();

    switch (this.tableStyle) {
      // overwrite only settings that are different than default

      case TableStyle.LEGACY_DEFAULT:
        _config = {
          "frameless": false,
          "cellBackground": defaultColors.ig_color_white
        }
        break;

      case TableStyle.LEGACY_FRAMELESS:
        _config = {
          "frameless": true,
          "cellBackground": defaultColors.ig_color_white
        }
        break;
      case TableStyle.LEGACY_BORDERED_ROWS:
        _config = {
          "frameless": true,
          "rowBorder": true
        }
        break;
      case TableStyle.MODAL:
        _config = {
          "rowGap": 5,
          "rowHeight": 40,
          "cellPaddingX": 10,
          "cellPaddingY": 3,
          "tableHeight": '45vh',
//          "surroundingBorder": true,
          "frameless": true,
          "hoverShadow": false
        }
        break;
      case TableStyle.MODAL_FLUID:
        _config = {
          "rowGap": 5,
          "rowHeight": 40,
          "cellPaddingX": 10,
          "cellPaddingY": 3,
          "tableHeight": '45vh',
          "fluidTableHeight": true,
//          "surroundingBorder": true,
          "frameless": true,
          "hoverShadow": false
        }
        break;
      case TableStyle.LEGACY_HYBRID_CORE:
        _config = {
          "rowGap": 1,
          "cellPaddingX": 20,
          "cellBackground": defaultColors.ig_color_white,
          "backgroundColour": defaultColors.ig_color_card_background,
          "whiteHeader": true,
          "frameless": true
        }
        break;
      default:
      // nothing
    }
    this.tableStyleConfig = { ...this.tableStyleConfig, ..._config };

    this.setStyleProperties();
  }

  setStyleProperties() {
    if (this.tableStyleConfig.tableHeight && this.tableStyleConfig.tableHeight !== 'auto') {
      this.wrapperLevel0?.nativeElement.style.setProperty("--ig-table-height", this.tableStyleConfig.tableHeight);
    }
  }

  onSearch() {
    this.doSearch.emit(this.search);
  }
  onSelectionChange(selection: T[]) {
    this.selection = selection;
    this.selectionChange.emit(this.selection);
  }
  onLoadNext() {
    // check if we have something to load next
    if (this.meta?.page !== undefined && this.meta?.limit !== undefined && this.meta?.count !== undefined && this.meta?.total !== undefined) {
      if (((this.meta.page * this.meta.limit) + this.meta.count) < this.meta.total) {
        this.loadNext.emit();
      }
    } else {
      this.loadNext.emit();
    }

  }


  resetSearch(): void {
    this.search = '';
    this.onSearch();
  }


  onToggleFavorites() {
    this.filterFavorites = !this.filterFavorites;
    this.selection = [];
    //this.ref.markForCheck();
    this.selectionChange.emit(this.selection);
    this.toggleFavorites.emit(this.toggleFavoritesEnabled);
  }


}
